/**
 * Desktop widths for the sell-screen dialogs.
 *
 * The problem this solves, measured on the Product Details dialog:
 *
 *   card  (sellscreenModal pickerCard)  max-width 560
 *   body  (minWidth: width * 0.5 @1600) min-width 800
 *
 * A child whose min-width exceeds its parent's max-width does not shrink and
 * does not wrap - it overflows. So the inventory chips, the offers panel and
 * the Add to Cart button all ran out past the right edge of the white card,
 * and on a narrower window the card grew a horizontal scrollbar instead.
 *
 * Both halves of that conflict were mine: the 560 ceiling was added to stop
 * percentage-width dialogs turning into 1500px panels on a wide monitor, and
 * it collided with body widths that were already viewport-relative.
 *
 * The fix is to stop deriving a *content* width from the window at all on
 * desktop. A dialog is as wide as its content needs, in two steps, clamped so
 * it can still shrink into a small browser window. The card's ceiling
 * (webDialog.maxWidth) is defined as the larger step plus the card's padding,
 * so the two can no longer disagree.
 *
 * Applied *after* each dialog's own style, so it replaces the percentage on
 * web only; `dialogWeb.ts` returns null, which react-native ignores, leaving
 * Android on its existing percentages.
 */

import {StyleProp, ViewStyle} from 'react-native';

import {webDialog} from './webTokens';

export function webDialogWidth(
  size: 'sm' | 'md' | 'lg',
  viewportWidth: number,
): StyleProp<ViewStyle> {
  const available = viewportWidth - webDialog.viewportInset * 2;
  const width = Math.max(
    webDialog.minWidth,
    Math.min(webDialog[size], available),
  );

  /*
   * minWidth and maxWidth to the same value rather than `width`: the body sits
   * in a flex column, and pinning both ends means neither the card nor the
   * content can talk it into a different measure - which is what 15.4 asks for
   * when it says a dialog's width must be stable.
   */
  return {minWidth: width, maxWidth: width};
}
